import {
  CartItemType,
  CompanyLineItemVisibility,
  calculateInvoiceTotals,
  DEFAULT_TAX_ROUNDING_STRATEGY,
  Guid,
  InvoiceCartItem,
  invoicePaymentRecordsToInvoicePayments,
  invoiceV2ToInvoiceV1Status,
  LOAN_PAYMENT_COMPLETED_STATUSES,
  noOp,
  paymentMethodDisplayName,
  R,
  usCentsToUsd,
} from '@breezy/shared'
import { faDownload } from '@fortawesome/pro-light-svg-icons'
import { faEllipsis, faPrint } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'antd'
import classNames from 'classnames'
import React, { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSearchParam } from 'react-use'
import { useSubscription } from 'urql'
import { z } from 'zod'
import { LoanPromoShortDisclaimer } from '../../../components/Financing/LoanPromo/LoanPromoDisclaimer'
import { LoadingSpinner } from '../../../components/LoadingSpinner'
import { PaymentStatusProvider } from '../../../components/PaymentWorkflow/providers/PaymentStatusProvider'
import { ServiceLocationReadError } from '../../../components/ServiceLocationReadError'
import { isQueryInitiallyLoading } from '../../../components/TrpcQueryLoader'
import { trpc } from '../../../hooks/trpc'
import { useIsMediumScreen } from '../../../hooks/useIsMobile'
import { UnauthPricebookSettingsProvider } from '../../../hooks/usePricebookSettings'
import { FinancialConfigContext } from '../../../providers/CompanyFinancialConfigWrapper'
import { MobileActionBar, MobileActionBarButton } from '../../../ui/ActionBar/MobileActionBar'
import { useModalState, useStrictContext } from '../../../utils/react-utils'
import { SelfServePaymentFormV2 } from '../../SelfServePaymentPage/SelfServePaymentFormV2'
import { SelfServePaymentPayButtonFooter } from '../../SelfServePaymentPage/SelfServePaymentPayButtonFooter'
import {
  SelfServePaymentFormContext,
  SelfServePaymentFormContextInvoice,
} from '../../SelfServePaymentPage/selfServePaymentUtils'
import { InvoiceContent } from '../components/InvoiceContent'
import { resolveServiceCompletionDateForDisplay } from '../invoiceDates'
import {
  InvoiceContext,
  InvoiceDataContext,
  InvoiceDataContextType,
  InvoiceInfo,
  useFetchedDiscountsToInvoiceDiscounts,
  useIsInvoiceFinanceable,
} from '../invoiceUtils'
import {
  ANON_RELEVANT_INVOICE_DATA_SUBSCRIPTION,
  ANON_REVIEW_INVOICE,
  ReviewInvoice,
  SimpleLoanRecord,
} from './ReviewInvoice.anon-gql'
import ReviewInvoiceActionModal, { useDownloadReviewInvoice, usePrintReviewInvoice } from './ReviewInvoiceActionModal'

const NotFound = () => (
  <div className="flex h-screen w-screen items-center justify-center">
    <div className="text-center text-base font-semibold">
      The requested invoice cannot be found.
      <br />
      <br />
      If you believe you should be able to see this invoice, please request a fresh invoice link from your Pro.
    </div>
  </div>
)

const InvoiceDisclaimerSection = React.memo(() => {
  const { disclaimer } = useStrictContext(InvoiceContext)

  const disclaimerText =
    disclaimer?.trim() ||
    'Payment is due according to the terms stated on this invoice. Late payments may be subject to fees. All services rendered are warrantied according to our company policy. For warranty claims or questions regarding this invoice, please contact us using the information provided above. Any disputes must be reported within 30 days of the invoice date. By accepting our services, you agree to these terms and conditions.'

  return (
    <div className="mt-8 border-t border-bz-gray-300 pt-4 text-xs text-bz-gray-800">
      <p className="mb-2 font-semibold">Terms & Conditions:</p>
      <p className="whitespace-pre-wrap">{disclaimerText}</p>
    </div>
  )
})

type ReviewInvoicePageWithEverythingProps = {
  invoice: ReviewInvoice
  lineItemVisibilityStatus: CompanyLineItemVisibility
}

const ReviewInvoicePageWithEverything = React.memo<ReviewInvoicePageWithEverythingProps>(
  ({ invoice, lineItemVisibilityStatus }) => {
    const isMediumScreen = useIsMediumScreen()

    const [actionsModalOpen, openActionsModal, closeActionsModal] = useModalState()

    const { downloadInvoicePDF, invoiceDownloading } = useDownloadReviewInvoice(invoice)

    const { beginOpenInvoice, invoiceGenerating } = usePrintReviewInvoice(invoice)

    const onDownloadPdf = useCallback(async () => {
      await downloadInvoicePDF()
    }, [downloadInvoicePDF])

    const onPrintInvoice = useCallback(async () => {
      await beginOpenInvoice()
    }, [beginOpenInvoice])
    const { invoiceTotals } = useStrictContext(InvoiceDataContext)
    const isFinanceable = useIsInvoiceFinanceable(invoice.status, invoiceTotals)
    return (
      <div
        className={classNames(
          'min-w-screen self-serve-payment-page flex min-h-screen items-stretch justify-center bg-[#F1F5F9] text-base *:min-w-0 *:flex-1',
          isMediumScreen ? 'flex-col' : 'flex-row',
        )}
      >
        <div
          className={classNames(
            'mx-auto flex max-w-[850px] flex-col bg-white',
            isMediumScreen ? 'p-4 pb-[76px]' : 'p-10',
          )}
        >
          <div
            className={classNames(
              'flex flex-row items-start gap-2 border-0 border-b border-solid border-bz-border-secondary',
              isMediumScreen ? 'mb-4 pb-4 *:flex-1 *:basis-0' : 'mb-6 pb-6',
            )}
          >
            <Button
              size="large"
              icon={<FontAwesomeIcon icon={faDownload} />}
              loading={invoiceDownloading}
              onClick={onDownloadPdf}
            >
              Download
            </Button>
            <Button
              size="large"
              icon={<FontAwesomeIcon icon={faPrint} />}
              loading={invoiceGenerating}
              onClick={onPrintInvoice}
            >
              Print
            </Button>
          </div>
          <InvoiceContent
            customerFacing
            noOuterBorder
            lineItemVisibilityStatus={lineItemVisibilityStatus}
            footer={
              <>
                {isFinanceable && <LoanPromoShortDisclaimer amountUsc={invoiceTotals.dueUsc} />}
                {isMediumScreen && (
                  <MobileActionBar>
                    <MobileActionBarButton icon={faEllipsis} onClick={openActionsModal}>
                      More
                    </MobileActionBarButton>
                  </MobileActionBar>
                )}
                <InvoiceDisclaimerSection />
              </>
            }
          />
        </div>
        {actionsModalOpen && <ReviewInvoiceActionModal invoice={invoice} onCancel={closeActionsModal} />}
        {isMediumScreen ? <SelfServePaymentPayButtonFooter /> : <SelfServePaymentFormV2 />}
      </div>
    )
  },
)

type ReviewInvoicePageWithInvoiceDataProps = ReviewInvoicePageWithGuidProps & {
  invoice: ReviewInvoice
}

const ReviewInvoicePageWithInvoiceData = React.memo<ReviewInvoicePageWithInvoiceDataProps>(
  ({ invoiceGuid, invoice, contactGuid }) => {
    const serviceLocation = trpc.invoice['unauth:invoicing:invoice:service-location:read'].useQuery(
      { invoiceGuid },
      { staleTime: 0, refetchOnMount: 'always' },
    )
    const lineItems = useMemo<InvoiceCartItem[]>(
      () =>
        invoice.invoiceCartItems.map(({ cartItem: { photo, ...cartItem }, seq }) => ({
          ...cartItem,
          seq,
          description: cartItem.description ?? '',
          // None of these things are useful in readonly so I'm not fetching them. Putting dummy values in for
          // TypeScript
          itemGuid: '',
          itemType: CartItemType.SERVICE,
          photoGuid: photo?.photoGuid,
          photoCdnUrl: photo?.cdnUrl,
        })),
      [invoice.invoiceCartItems],
    )
    const discounts = useFetchedDiscountsToInvoiceDiscounts(invoice.invoiceDiscounts)

    const info = useMemo<InvoiceInfo>(() => {
      return {
        ...invoice,
        serviceAddress:
          !serviceLocation.isFetching && !serviceLocation.isError
            ? (serviceLocation.data?.address ?? invoice.serviceAddress)
            : invoice.serviceAddress,
        serviceLocationName:
          !serviceLocation.isFetching && !serviceLocation.isError ? serviceLocation.data?.displayName : undefined,
        customerPurchaseOrderNumber: invoice.customerPurchaseOrderNumber ?? '',
        displayId: invoice.displayIdV2 ?? invoice.displayId.toString(),
        serviceCompletionDate: resolveServiceCompletionDateForDisplay(invoice.serviceCompletionDate),
        billingAddress: invoice.billingAddress
          ? {
              type: 'adhoc',
              adHocAddress: invoice.billingAddress,
            }
          : {
              type: 'same_as_service',
            },
        resolvedBillingAddress: invoice.billingAddress,
        serviceContact: invoice.jobLink?.job.pointOfContact ?? invoice.account.accountContacts[0]?.contact,
        issuedAt: invoice.issuedAt,
        dueAt: invoice.dueAt,
        // I know based on business logic that this will always be defined
        serviceLocationGuid: invoice.locationLink?.locationGuid ?? '',
      }
    }, [invoice, serviceLocation.data, serviceLocation.isFetching, serviceLocation.isError])

    const invoiceTotals = useMemo(() => {
      return calculateInvoiceTotals(
        lineItems,
        invoice.taxRate,
        discounts,
        invoicePaymentRecordsToInvoicePayments(invoice.invoicePayments),
        invoice.dynamicPricingType,
        {
          taxRoundingStrategy: invoice.taxConfiguration?.taxRoundingStrategy ?? DEFAULT_TAX_ROUNDING_STRATEGY,
        },
      )
    }, [
      discounts,
      invoice.dynamicPricingType,
      invoice.invoicePayments,
      invoice.taxConfiguration?.taxRoundingStrategy,
      invoice.taxRate,
      lineItems,
    ])

    const paymentCompleted = useMemo(() => {
      return invoiceTotals.dueUsc === 0
    }, [invoiceTotals.dueUsc])

    const loanRecord = useMemo<SimpleLoanRecord | undefined>(() => {
      return invoice.wisetackLoanRecords[0]
    }, [invoice.wisetackLoanRecords])

    const loanApplicationStarted = useMemo(() => {
      return !!loanRecord?.loanApplicationLink
    }, [loanRecord?.loanApplicationLink])

    const loanPaymentSucceeded = useMemo(
      () =>
        !!loanRecord?.wisetackLoanStatuses[0].loanStatus &&
        LOAN_PAYMENT_COMPLETED_STATUSES.includes(loanRecord.wisetackLoanStatuses[0].loanStatus),
      [loanRecord?.wisetackLoanStatuses],
    )

    const [didPaymentSucceed, setDidPaymentSucceed] = useState(false)
    const setPaymentSuccessStatus = useCallback((didPaymentSucceed: boolean) => {
      setDidPaymentSucceed(didPaymentSucceed)
    }, [])

    const [{ data: relevantData, fetching: fetchingRelevantData }] = useSubscription({
      query: ANON_RELEVANT_INVOICE_DATA_SUBSCRIPTION,
      variables: {
        companyGuid: invoice.companyGuid,
      },
    })
    const shouldFetchInvoiceTechnicianDisplay =
      relevantData?.companiesByPk?.companyConfig?.invoiceDisplayTechnicianName ?? false
    // Keep the anonymous GraphQL payload free of raw assignment/user data.
    // When enabled, ask the backend for the sanitized disclosure string instead.
    const invoiceTechnicianDisplayQuery = trpc.invoice['unauth:invoicing:invoicev2:get-technician-display'].useQuery(
      {
        invoiceGuid,
      },
      {
        enabled: shouldFetchInvoiceTechnicianDisplay,
      },
    )

    const invoiceData = useMemo<InvoiceDataContextType>(
      () => ({
        messageHtml: invoice.messageHtml ?? '',
        taxRate: {
          // The name and guid aren't relevant for this readonly flow so I don't fetch them.
          pricebookTaxRateGuid: '',
          name: '',
          rate: invoice.taxRate,
        },
        info,
        lineItems,
        discounts,
        invoiceTotals,
        dynamicPricingType: invoice.dynamicPricingType,
        taxConfiguration: {
          roundingStrategy: invoice.taxConfiguration?.taxRoundingStrategy ?? DEFAULT_TAX_ROUNDING_STRATEGY,
        },
        hideLineItemPrices: invoice.hideLineItemPrices ?? false,
      }),
      [
        discounts,
        info,
        invoice.dynamicPricingType,
        invoice.hideLineItemPrices,
        invoice.messageHtml,
        invoice.taxConfiguration?.taxRoundingStrategy,
        invoice.taxRate,
        invoiceTotals,
        lineItems,
      ],
    )

    const customAmountDueUsd = useSearchParam('amountDueUsd')

    const paymentAmountDueUsd = useMemo(() => {
      const invoiceAmountDueUsc = usCentsToUsd(invoiceTotals.dueUsc)
      const amountDueUsd = customAmountDueUsd ? parseFloat(customAmountDueUsd) : undefined

      if (amountDueUsd) {
        return Math.min(amountDueUsd, invoiceAmountDueUsc)
      }

      return invoiceAmountDueUsc
    }, [customAmountDueUsd, invoiceTotals.dueUsc])

    const paymentContextInvoice = useMemo<SelfServePaymentFormContextInvoice>(
      () => ({
        invoiceGuid,
        displayId: invoice.displayIdV2 ?? invoice.displayId.toString(),
        companyGuid: invoice.companyGuid,
        accountGuid: invoice.accountGuid,
        totalPriceUsd: usCentsToUsd(invoiceTotals.totalUsc),
        // Reference numbers aren't a thing in invoices V2. They are used by payments, however. A payment makes its
        // reference number `${invoice reference number}-${payment number}`. But it's just a string so we can just use
        // the display id instead.
        referenceNumber: invoice.displayIdV2 ?? invoice.displayId.toString(),
        status: invoiceV2ToInvoiceV1Status(invoice.status),
        paymentsSummary: {
          amountDueUsd: paymentAmountDueUsd,
          totalPaidAmountUsd: usCentsToUsd(invoiceTotals.paidUsc),
          paymentTypesDescription: R.uniq(
            invoice.invoicePayments.flatMap(ip => paymentMethodDisplayName(ip.paymentRecord.paymentMethod)),
          ).join(', '),
          lastPaidAt: invoice.invoicePayments[0]?.paymentRecord.occurredAt,
        },
        links: {
          jobGuid: invoice.jobLink?.jobGuid,
          maintenancePlanGuid: invoice.maintenancePlanLink?.maintenancePlanGuid,
        },
      }),
      [
        invoice.accountGuid,
        invoice.companyGuid,
        invoice.displayId,
        invoice.displayIdV2,
        invoice.jobLink?.jobGuid,
        invoice.maintenancePlanLink?.maintenancePlanGuid,
        invoice.invoicePayments,
        invoice.status,
        invoiceGuid,
        invoiceTotals.paidUsc,
        invoiceTotals.totalUsc,
        paymentAmountDueUsd,
      ],
    )

    const isInvoiceTechnicianDisplayLoading =
      shouldFetchInvoiceTechnicianDisplay &&
      (invoiceTechnicianDisplayQuery.isLoading ||
        isQueryInitiallyLoading(invoiceTechnicianDisplayQuery) ||
        (invoiceTechnicianDisplayQuery.isFetching && !invoiceTechnicianDisplayQuery.data))

    if (fetchingRelevantData || isInvoiceTechnicianDisplayLoading) {
      return (
        <div className="flex h-screen w-screen items-center justify-center">
          <LoadingSpinner />
        </div>
      )
    }
    if (!(relevantData?.companiesByPk?.companyConfig && relevantData?.companiesByPk?.billingProfile)) {
      return <NotFound />
    }

    const wisetackEnabled = !!relevantData.companiesByPk.billingProfile.wisetackMerchantId && !!contactGuid

    return (
      <PaymentStatusProvider>
        <InvoiceDataContext.Provider value={invoiceData}>
          <ServiceLocationReadError isError={serviceLocation.isError} retry={serviceLocation.refetch} />
          <InvoiceContext.Provider
            value={{
              ...invoice,
              displayId: invoice.displayIdV2 ?? invoice.displayId.toString(),
              accountReferenceNumber: invoice.account?.accountReferenceNumber ?? undefined,
              customerNumber: invoice.account?.customerNumber ?? undefined,
              ...relevantData.companiesByPk,
              jobGuid: invoice.jobLink?.jobGuid,
              maintenancePlanGuid: invoice.maintenancePlanLink?.maintenancePlanGuid,
              companyBlurb: relevantData.companiesByPk.companyConfig.companyBlurb ?? '',
              ...relevantData.companiesByPk.billingProfile,
              businessEmail: relevantData.companiesByPk.billingProfile.emailAddress.emailAddress,
              businessPhoneNumber: relevantData.companiesByPk.billingProfile.phoneNumber.phoneNumber,
              disclaimer: relevantData.companiesByPk.billingProfile.invoiceDisclaimer,
              invoiceTechnicianDisplayName: shouldFetchInvoiceTechnicianDisplay
                ? invoiceTechnicianDisplayQuery.data?.technicianDisplayName
                : undefined,
              // Not needed in readonly mode
              defaultInvoiceTerm: 'DUE_ON_RECEIPT',
              realPricebookItemGuidMap: {},
              companyInvoiceIncludePaymentLink: false,
              customerNumberEnabled: relevantData.companiesByPk.companyConfig?.customerNumberEnabled ?? false,
              maintenancePlanSavingsEnabled: invoice.maintenancePlanSavingsEnabled,
              companyMaintenancePlanSavingsEnabled:
                relevantData.companiesByPk.companyConfig?.maintenancePlanSavingsEnabled ?? false,
              hideLineItemPrices: invoice.hideLineItemPrices,
              companyHideLineItemPrices: relevantData.companiesByPk.companyConfig?.hideLineItemPrices ?? false,
            }}
          >
            <SelfServePaymentFormContext.Provider
              value={{
                invoiceGuid,
                invoice: paymentContextInvoice,
                companyGuid: invoice.companyGuid,
                companyName: relevantData.companiesByPk.companyName,
                wisetackEnabled,
                paymentCompleted,
                loanApplicationStarted,
                loanPaymentSucceeded,
                didPaymentSucceed,
                contactGuid,
                loanRecord,
                setPaymentSuccessStatus,
                tzId: relevantData.companiesByPk.tzId,
                // This is there for the old version, but this version uses a live query so it shouldn't be necessary
                refetchInvoice: noOp,
              }}
            >
              <FinancialConfigContext.Provider
                value={{
                  wisetackEnabled,
                  wisetackMerchantId: relevantData.companiesByPk.billingProfile.wisetackMerchantId,
                }}
              >
                <ReviewInvoicePageWithEverything
                  invoice={invoice}
                  lineItemVisibilityStatus={
                    relevantData.companiesByPk.companyConfig.invoiceLineItemsVisibility ?? 'VISIBLE'
                  }
                />
              </FinancialConfigContext.Provider>
            </SelfServePaymentFormContext.Provider>
          </InvoiceContext.Provider>
        </InvoiceDataContext.Provider>
      </PaymentStatusProvider>
    )
  },
)

type ReviewInvoicePageWithGuidProps = {
  invoiceGuid: Guid
  contactGuid?: Guid
}

const ReviewInvoicePageWithGuid = React.memo<ReviewInvoicePageWithGuidProps>(({ invoiceGuid, contactGuid }) => {
  const [{ data: invoiceData, fetching: fetchingInvoiceData }] = useSubscription({
    query: ANON_REVIEW_INVOICE,
    variables: {
      invoiceGuid,
    },
  })

  if (fetchingInvoiceData) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }
  if (!invoiceData?.invoicesByPk) {
    return <NotFound />
  }

  return (
    <UnauthPricebookSettingsProvider companyGuid={invoiceData.invoicesByPk.companyGuid}>
      <ReviewInvoicePageWithInvoiceData
        invoiceGuid={invoiceGuid}
        invoice={invoiceData.invoicesByPk}
        contactGuid={contactGuid}
      />
    </UnauthPricebookSettingsProvider>
  )
})

export const ReviewInvoicePage = React.memo(() => {
  const { invoiceGuid, contactGuid } = useParams<{
    invoiceGuid?: string
    contactGuid?: string
  }>()

  if (!invoiceGuid) {
    return <NotFound />
  }

  if (!z.string().uuid().safeParse(invoiceGuid).success) {
    return <NotFound />
  }

  return <ReviewInvoicePageWithGuid invoiceGuid={invoiceGuid} contactGuid={contactGuid} />
})

ReviewInvoicePage.displayName = 'ReviewInvoicePage'
